/**
 * Pure Account policy seam for Acceptable Progress Termination phases.
 * PGM Progress Surfaces and purchase entry points consume this; they do not
 * re-encode phase rules.
 *
 * Phase sketch:
 *   Acceptable Progress Termination?
 *     no  -> notApplicable
 *     yes -> Terminated Level Complete?
 *             no  -> progressPrivileged
 *             yes -> Cleared to Continue?
 *                     no  -> reapplyGated
 *                     yes -> clearedToContinue
 *
 * Cleared to Continue requires approved reapplication or eligibility restored.
 * Application submitted alone is not clearance — only those two inputs clear.
 * When eligibility and standing disagree, standing that is no longer Acceptable
 * Progress Termination wins (notApplicable). Terminal Level Complete (no
 * next-level purchase) still maps to reapplyGated with showReapplyPathOffer.
 * Consumers must pass explicit booleans for standing and Terminated Level
 * Complete. Missing/`undefined` values must not unlock purchase or mis-phase
 * the offer (fail closed via TypeError).
 */

export const PHASE = {
  NOT_APPLICABLE: "notApplicable",
  PROGRESS_PRIVILEGED: "progressPrivileged",
  REAPPLY_GATED: "reapplyGated",
  CLEARED_TO_CONTINUE: "clearedToContinue",
}

/**
 * @param {object} input
 * @param {boolean} input.isAcceptableProgressTerminated
 *   Live standing is Acceptable Progress Termination (terminate code GK).
 *   Must be an explicit boolean — undefined/null throws (does not unlock purchase).
 * @param {boolean} input.isTerminatedLevelComplete
 *   Current level requirements satisfied (same signal PGM surfaces already use).
 *   Includes Terminal Level Complete — still reapplyGated until cleared.
 *   Must be an explicit boolean — undefined/null throws.
 * @param {boolean} [input.isApplicationApproved=false]
 *   Reapplication approved — counts as Cleared to Continue.
 * @param {boolean} [input.isEligibilityRestored=false]
 *   Eligibility restored — counts as Cleared to Continue.
 * @returns {{
 *   phase: 'notApplicable'|'progressPrivileged'|'reapplyGated'|'clearedToContinue',
 *   showProgressStandingNotice: boolean,
 *   allowNextLevelPurchase: boolean,
 *   showReapplyPathOffer: boolean,
 * }}
 */
export function acceptableProgressStanding({
  isAcceptableProgressTerminated,
  isTerminatedLevelComplete,
  isApplicationApproved = false,
  isEligibilityRestored = false,
}) {
  if (typeof isAcceptableProgressTerminated !== "boolean") {
    throw new TypeError(
      "acceptableProgressStanding: isAcceptableProgressTerminated must be a boolean",
    )
  }

  if (typeof isTerminatedLevelComplete !== "boolean") {
    throw new TypeError(
      "acceptableProgressStanding: isTerminatedLevelComplete must be a boolean",
    )
  }

  if (!isAcceptableProgressTerminated) {
    return {
      phase: PHASE.NOT_APPLICABLE,
      showProgressStandingNotice: false,
      allowNextLevelPurchase: true,
      showReapplyPathOffer: false,
    }
  }

  if (!isTerminatedLevelComplete) {
    return {
      phase: PHASE.PROGRESS_PRIVILEGED,
      showProgressStandingNotice: true,
      allowNextLevelPurchase: false,
      showReapplyPathOffer: false,
    }
  }

  const isClearedToContinue = isApplicationApproved || isEligibilityRestored

  if (isClearedToContinue) {
    return {
      phase: PHASE.CLEARED_TO_CONTINUE,
      showProgressStandingNotice: false,
      allowNextLevelPurchase: true,
      showReapplyPathOffer: false,
    }
  }

  return {
    phase: PHASE.REAPPLY_GATED,
    showProgressStandingNotice: true,
    allowNextLevelPurchase: false,
    showReapplyPathOffer: true,
  }
}
